import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

// Subscription validation request interface
export interface SubscriptionValidationRequest {
  channelId?: string; // Optional channel ID
}

// Subscription validation response interface
export interface SubscriptionValidationResponse {
  success: boolean;
  data: {
    canCreateChannel: boolean;
    canCreateMessage: boolean;
    canCreateAttachment: boolean;
  };
  message: string;
  code: number;
}

// Module-level guard: only one validation request app-wide to prevent API loop
let inFlightPromise: Promise<SubscriptionValidationResponse> | null = null;
// Cooldown after a request completes so rapid repeated triggers don't start new requests
const VALIDATION_COOLDOWN_MS = 800;
let lastCompletedAt = 0;
let lastResult: SubscriptionValidationResponse | null = null;
let lastRequestKey = '';

/**
 * Validate subscription permissions for channel creation
 * @param userId - User ID for the validation endpoint
 * @param payload - Validation request payload with optional channelId
 * @returns Promise with validation response
 */
export const validateSubscription = async (
  userId: string,
  payload: SubscriptionValidationRequest = {}
): Promise<SubscriptionValidationResponse> => {
  const requestKey = `${userId}:${JSON.stringify(payload)}`;
  const now = Date.now();
  // Reuse recent result if same request within cooldown (stops re-render / effect loops)
  if (lastResult && requestKey === lastRequestKey && now - lastCompletedAt < VALIDATION_COOLDOWN_MS) {
    return lastResult;
  }
  // Reuse in-flight request so repeated callers get one API call
  if (inFlightPromise) {
    return inFlightPromise;
  }
  const run = async (): Promise<SubscriptionValidationResponse> => {
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || '';
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!userId) {
        throw new Error('User ID is required');
      }

      const url = `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.PAYMENT.ZOHO.VALIDATE_SUBSCRIPTION(userId)}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to validate subscription: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error('Subscription validation error:', error);
      throw error;
    } finally {
      inFlightPromise = null;
    }
  };
  const promise = run();
  inFlightPromise = promise; // Set immediately so concurrent callers reuse this request
  promise
    .then((res) => {
      lastResult = res;
      lastRequestKey = requestKey;
      lastCompletedAt = Date.now();
    })
    .catch(() => {
      lastCompletedAt = Date.now();
      lastResult = null; // Don't serve cached result after a failure
    });
  return promise;
};
